import React, { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Typography, Button, Stack, Drawer } from '@mui/material';
import { useAuth } from './AuthContext';
import { AppDetailsContent, AppDetails, resolveAppDetails } from './AppDetailsDrawer';

interface AppDetailsPageProps { isActive: boolean }

// Deep-link fallback for the `/home/app-details?host=…` route (e.g. an old
// bookmark loaded directly). In-app "view app details" links open the details
// slider over the current page via useAppDetailsDrawer() instead; this route
// renders the same slider on its own so a direct URL still works.
export const AppDetailsPage: React.FC<AppDetailsPageProps> = ({ isActive }) => {
  const { appData, fetchDashboardData } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const host = (searchParams.get('host') || '').trim();
  // Stable identifiers passed by callers so dual-platform / duplicate-host apps
  // resolve to the correct entry instead of whichever one host-matches first.
  const platformParam = (searchParams.get('platform') || '').trim().toLowerCase();
  const appIdParam = (searchParams.get('appId') || '').trim();

  useEffect(() => {
    if (isActive && (!appData || Object.keys(appData).length === 0)) {
      fetchDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  const app = useMemo<AppDetails | null>(
    () => resolveAppDetails(appData, host, platformParam, appIdParam),
    [host, appData, platformParam, appIdParam],
  );

  const goBack = () => { if (window.history.length > 1) navigate(-1); else navigate('/home/app-distribution'); };

  // Render app details as a right-side slider (the same drawer used across the
  // rest of the app) rather than a standalone full page. Closing it returns to
  // whichever page opened the link.
  return (
    <Drawer
      anchor="right"
      open={isActive}
      onClose={goBack}
      slotProps={{ backdrop: { sx: { backdropFilter: 'blur(6px)', backgroundColor: 'rgba(15, 23, 42, 0.3)' } } }}
      PaperProps={{ sx: { width: { xs: '100%', sm: 600 }, borderRadius: 0 } }}
    >
      {app ? (
        <AppDetailsContent app={app} onClose={goBack} />
      ) : (
        <Stack alignItems="center" spacing={1.5} sx={{ py: 8, px: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>App not found</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            {host ? `No app matched host "${host}".` : 'No app specified.'}
          </Typography>
          <Button onClick={goBack} variant="outlined" sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}>Back</Button>
        </Stack>
      )}
    </Drawer>
  );
};

export default AppDetailsPage;
